import React from 'react';
import { CheckCircle, XCircle, Clock, MessageSquare, RotateCcw, Send, User } from 'lucide-react';

/**
 * ApprovalTimeline Component
 * Visual timeline showing the approval journey of a proposal
 * Displays events chronologically with icons, timestamps, and comments
 */
export default function ApprovalTimeline({ proposal }) {
  if (!proposal) return null;

  // Build timeline events from proposal history
  const buildTimelineEvents = () => {
    const events = [];

    // 1. Creation event
    events.push({
      type: 'created',
      timestamp: proposal.created_at,
      actor: proposal.created_by_name || proposal.account_executive_name || 'Unknown',
      icon: User,
      color: 'blue',
      title: 'Proposal Created',
      description: `${proposal.scenarios?.length || 0} scenario(s) added`
    });

    // 2. Submission event
    if (proposal.submitted_at) {
      const submitterName = proposal.submitter_name || proposal.submitted_by || proposal.created_by;
      const onBehalfOf = proposal.submitted_on_behalf_of_name;
      const actualSubmitter = proposal.actual_submitter_name;
      
      let actor = submitterName;
      let description = proposal.submission_comments || 'Pending review by approvers';
      
      // Show transparency if submitted on behalf of someone
      if (onBehalfOf && actualSubmitter) {
        actor = actualSubmitter;
        description = `Submitted on behalf of ${onBehalfOf}. ${description}`;
      }
      
      events.push({
        type: 'submitted',
        timestamp: proposal.submitted_at,
        actor: actor,
        icon: Send,
        color: 'indigo',
        title: onBehalfOf ? `Submitted on Behalf of ${onBehalfOf}` : 'Submitted for Approval',
        description: description,
        onBehalfOf: onBehalfOf
      });
    }

    // 3. Approval/Rejection events
    if (proposal.approval_history && Array.isArray(proposal.approval_history)) {
      proposal.approval_history.forEach((event) => {
        events.push({
          type: event.action, // 'approved' or 'rejected'
          timestamp: event.timestamp,
          actor: event.approver_name || event.approver_email,
          icon: event.action === 'approved' ? CheckCircle : XCircle,
          color: event.action === 'approved' ? 'green' : 'red',
          title: event.action === 'approved' ? 'Approved' : 'Rejected',
          description: event.comments || 'No comments provided',
          role: event.approver_role
        });
      });
    }

    // 4. Reopen event
    if (proposal.reopened_at) {
      events.push({
        type: 'reopened',
        timestamp: proposal.reopened_at,
        actor: proposal.reopened_by_name || proposal.reopened_by || 'Unknown',
        icon: RotateCcw,
        color: 'amber',
        title: 'Reopened for Revision',
        description: proposal.reopen_reason || 'Returned to draft for changes'
      });
    }

    // 5. Comments events
    if (proposal.comments && Array.isArray(proposal.comments)) {
      proposal.comments.forEach((comment) => {
        events.push({
          type: 'comment',
          timestamp: comment.created_at,
          actor: comment.author_name || comment.author_email,
          icon: MessageSquare,
          color: 'slate',
          title: 'Comment Added',
          description: comment.text,
          role: comment.author_role
        });
      });
    }

    // 6. Final approval (all approvers done)
    if (proposal.status === 'approved' && proposal.approved_at) {
      events.push({
        type: 'final_approved',
        timestamp: proposal.approved_at,
        actor: 'System',
        icon: CheckCircle,
        color: 'emerald',
        title: 'Fully Approved',
        description: 'All required approvals received. Ready for client presentation.',
        isFinal: true
      });
    }

    // Sort by timestamp (most recent first for timeline display)
    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const events = buildTimelineEvents();

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No timeline events yet</p>
      </div>
    );
  }

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500 border-blue-200',
      indigo: 'bg-indigo-500 border-indigo-200',
      green: 'bg-green-500 border-green-200',
      emerald: 'bg-emerald-500 border-emerald-200',
      red: 'bg-red-500 border-red-200',
      amber: 'bg-amber-500 border-amber-200',
      slate: 'bg-slate-500 border-slate-200'
    };
    return colors[color] || colors.slate;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-slate-600" />
        <h3 className="text-lg font-bold text-slate-900">Approval Timeline</h3>
        <span className="text-xs text-slate-500 ml-2">({events.length} events)</span>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

        {/* Events */}
        <div className="space-y-4">
          {events.map((event, idx) => {
            const Icon = event.icon;
            const isLast = idx === events.length - 1;

            return (
              <div key={idx} className="relative flex gap-4 group">
                {/* Icon circle */}
                <div className="relative z-10 flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full border-4 border-white shadow-md flex items-center justify-center ${getColorClasses(event.color)} ${event.isFinal ? 'ring-4 ring-emerald-200' : ''}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {!isLast && (
                    <div className="absolute top-12 left-1/2 -translate-x-1/2 w-0.5 h-4 bg-slate-200" />
                  )}
                </div>

                {/* Event content */}
                <div className="flex-1 pb-8">
                  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm group-hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {event.title}
                          {event.isFinal && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                              Final
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                          by <span className="font-medium text-slate-700">{event.actor}</span>
                          {event.role && (
                            <span className="ml-1 text-slate-400">({event.role})</span>
                          )}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatTimestamp(event.timestamp)}
                      </span>
                    </div>

                    {/* Description */}
                    {event.description && (
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {event.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status indicator at bottom */}
      {proposal.status === 'pending_approval' && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <Clock className="w-4 h-4 text-amber-600" />
          <p className="text-xs text-amber-800">
            <strong>Awaiting Approval:</strong> This proposal is currently under review by approvers.
          </p>
        </div>
      )}
    </div>
  );
}
